import React, { useEffect, useMemo, useState } from "react";
import ConnectorCard from "./ConnectorCard";
import SiteMetricCurve from "./SiteMetricCurve";
import { StatusText, getStatusClasses, getStatusChipStyleVars, getStatusLabel } from "../../helpers/StatusMapper";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useAuth } from "../../providers/AuthContext";
import toast from "react-hot-toast";
import { getSiteStatisticsPower15min, getSitesForStatistic } from "../../api/Telemetry";
import { useTranslation } from "react-i18next";

export function normalizeStatusText(raw = "") {
    if (!raw) return StatusText.DEFAULT;
    const s = String(raw).trim();
    if (s.toLowerCase() === "avaliable") return StatusText.AVAILABLE;
    return s;
}

function SiteLegend({ site }) {
    const { t } = useTranslation();
    const legend = site?.siteStatuses ?? [];
    if (!Array.isArray(legend) || legend.length === 0) return null;

    return (
        <div className="mt-3 flex flex-wrap gap-2">
            {legend.map((item, idx) => {
                const raw = item?.status ?? StatusText.DEFAULT;
                const normalizedForUI = normalizeStatusText(raw);

                const val =
                    typeof item?.percentage === "string"
                        ? Number(String(item.percentage).replace(",", "."))
                        : Number(item?.percentage);

                const pct = Number.isFinite(val) ? val : 0;

                return (
                    <span
                        key={`${raw}-${idx}`}
                        className={`${getStatusClasses(normalizedForUI, false)} shadow-md`}
                        style={getStatusChipStyleVars(normalizedForUI)}
                    >
                        <span>{getStatusLabel(normalizedForUI, t)}</span>
                        <span className="ml-1">{pct.toFixed(0)}%</span>
                    </span>
                );
            })}
        </div>
    );
}

function toLocalDayRangeIso(startDate, endDate) {
    if (!startDate || !endDate) return { startDateTime: null, endDateTime: null };

    const startLocal = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
        0,
        0,
        0,
        0
    );

    const endLocal = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate(),
        23,
        59,
        59,
        999
    );

    return {
        startDateTime: startLocal.toISOString(),
        endDateTime: endLocal.toISOString(),
    };
}

function normalizePowerResponse(resp) {
    if (Array.isArray(resp?.sitesPowerPeakValues)) return resp.sitesPowerPeakValues;
    return [];
}

function normalizeSitesResponse(resp) {
    if (Array.isArray(resp?.sites)) return resp.sites;
    if (Array.isArray(resp)) return resp;
    return [];
}

export default function SiteStatisticsContent({
    sitePublicIds,
    startDate,
    endDate,
    isFilterLoading,
}) {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();

    const hasSites = (sitePublicIds?.length ?? 0) > 0;

    const { startDateTime, endDateTime } = useMemo(
        () => toLocalDayRangeIso(startDate, endDate),
        [startDate, endDate]
    );

    const siteIdsKey = useMemo(
        () => JSON.stringify(sitePublicIds ?? []),
        [sitePublicIds]
    );

    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

 

    useEffect(() => {
        if (!hasSites) {
            setSites([]);
            setLoading(false);
            setError(null);
            return;
        }
        if (!startDateTime || !endDateTime) return;

        let cancelled = false;

        (async () => {
            try {
                setLoading(true);
                setError(null);

                const token = await getAccessToken();

                const powerPromise = getSiteStatisticsPower15min(token, {
                    sitePublicIds,
                    startDateTime,
                    endDateTime,
                });

                const sitesPromise = getSitesForStatistic(token, {
                    sitePublicIds,
                    startDateTime,
                    endDateTime,
                });

                const [powerResp, sitesResp] = await Promise.all([
                    powerPromise,
                    sitesPromise,
                ]);

                if (cancelled) return;

                const powerSites = normalizePowerResponse(powerResp);
                const baseSites = normalizeSitesResponse(sitesResp);

                const powerById = new Map(powerSites.map(p => [p.sitePublicId, p]));
                const baseById = new Map(baseSites.map(b => [b.sitePublicId, b]));

                const merged = (sitePublicIds ?? []).map((id) => {
                    const p = powerById.get(id);
                    const b = baseById.get(id);

                    return {
                        sitePublicId: id,
                        siteName: b?.siteName ?? p?.siteName ?? "Unknown site",
                        connectors: Array.isArray(b?.connectors) ? b.connectors : [],
                        siteStatuses: Array.isArray(b?.siteStatuses) ? b.siteStatuses : [],
                        series: Array.isArray(p?.series) ? p.series : [], 
                    };
                });

                // Om du vill: filtrera bort helt okända ids
                const filtered = merged.filter(s => s.siteName !== "Unknown site" || s.connectors.length || s.series.length);

                setSites(filtered);
            } catch (e) {
                if (cancelled) return;
                const msg = e?.message ?? t("siteStats.failedFetch");
                setError(msg);
                setSites([]);
                toast.error(msg);
            } finally {
                if (cancelled) return;
                setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasSites, siteIdsKey, startDateTime, endDateTime, getAccessToken]);

    if (isFilterLoading) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm mt-4">
                <LoadingSpinner text={t("siteStats.loadingSites")} />
            </div>
        );
    }
    if (!hasSites) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm mt-4">
                {t("siteStats.chooseSitePrefix")} <b>{t("entity.chargingSite")}</b> {t("siteStats.chooseSiteSuffix")}
            </div>
        );
    }

    if (loading) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm mt-4">
                <LoadingSpinner text={t("siteStats.loading")} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-red-200 bg-white p-6 text-sm mt-4">
                {error}
            </div>
        );
    }

    if (sites.length === 0) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm mt-4">
                {t("siteStats.noData")}
            </div>
        );
    }

    return (
        <div className="mt-4">
            {sites.map((site) => (
                <section
                    key={site.sitePublicId}
                    className="rounded-2xl border border-slate-200 bg-white p-5 shadow-lg mb-4"
                >
                    <div className="flex flex-col gap-2 border-b border-slate-200 pb-3">
                        <div className="flex justify-between items-start">
                            <div>
                                <h3>{site.siteName === "Unknown site" ? t("siteStats.unknownSite") : site.siteName}</h3>
                                <div className="text-sm">{t("siteStats.siteId", { id: site.sitePublicId })}</div>
                            </div>

                            <span className="text-sm">
                                {t("siteStats.connectorsCount", { count: (site.connectors ?? []).length })}
                            </span>
                        </div>

                        <SiteLegend site={site} />
                    </div>

                    {(site.connectors ?? []).length > 0 && (
                        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
                            {site.connectors.map((connector) => (
                                <ConnectorCard
                                    key={`${site.sitePublicId}-${connector.connectorPublicId}`}
                                    connector={connector}
                                />
                            ))}
                        </div>
                    )}

                    <div className="mt-4">
                        <SiteMetricCurve
                            rows={site.series}
                            title={t("siteStats.peakPower")}
                            unit="KW"
                            infoText={t("siteStats.peakPowerInfo")}
                        />
                    </div>
                </section>
            ))}
        </div>
    );
}
